import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDateWithTimeZoneFromUTC } from 'solaris-lens-common-lib/src/utils/dates';
import { getExternalLinkAccessPoolName } from 'solaris-lens-common-lib-web/src/utils/getExternalLinkAccessPoolName';

import styles from './styles.module.css';
import SuccessIcon from '@/assets/icons/success-icon.svg?react';
import UserIcon from '@/assets/icons/user.svg?react';
import {
  AdminCardBody,
  AdminCardButtons,
  AdminCardHeader
} from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import { Button } from '@/components/Form/Button';
import Input from '@/components/Form/Input';
import { POOL_NAME } from '@/constants';
import adminStyles from '@/containers/AdminToolContainers/styles.module.css';
import { useModal } from '@/context/ModalContext';
import { useNavigationBar } from '@/context/NavigationBarContext';
import { useToast } from '@/context/ToastContext';
import { useUpdateUser } from '@/hooks/services/Users';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { UserProfile, UserProfileApp } from '@/slices/types';
import { fetchUserProfile } from '@/slices/userProfileSlice';
import { RootState } from '@/store';
import { formatPhoneNumber } from '@/utils/inputPhone';

const DATE_FORMAT_WITH_TZ = 'MM/DD/YY hh:mm:ss A z';

const findCurrentApp = (
  apps: UserProfileApp[] = [],
  poolName: string
): UserProfileApp | undefined => {
  const accessPoolName = getExternalLinkAccessPoolName(poolName);
  return (
    apps.find(
      app => getExternalLinkAccessPoolName(app?.poolName) === accessPoolName
    ) || apps[0]
  );
};

const formatUpdatedAt = (app?: UserProfileApp): string => {
  if (!app?.updatedAt) return '';

  try {
    const dateObj = getDateWithTimeZoneFromUTC(app.updatedAt, undefined);
    const formatted = dateObj.format(DATE_FORMAT_WITH_TZ);
    if (!formatted || formatted.includes('Invalid')) return '';
    return formatted;
  } catch {
    return '';
  }
};

const getPhoneDigits = (phone: string): string => {
  if (!phone) return '';
  if (phone.startsWith('+1')) {
    return phone.replace('+1', '').replace(/\D/g, '');
  }
  const match = phone.match(/^\+\d{1,3}(.*)$/);
  if (match) {
    return match[1].replace(/\D/g, '');
  }
  return phone.replace(/\D/g, '');
};

const formatPhoneDisplay = (phone: string): string => {
  if (!phone) return '';
  if (phone.startsWith('+1')) {
    const digits = phone.replace('+1', '');
    return `+1 ${formatPhoneNumber(digits)}`;
  }
  return phone;
};

const validatePhone = (phone: string): boolean => {
  if (!phone) return true;
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10;
};

function UserProfileContainer() {
  const dispatch = useAppDispatch();
  const { setBreadcrumbOverride } = useNavigationBar();
  const { showToast } = useToast();
  const { updateModalContent, closeModal } = useModal();

  const userProfile = useSelector(
    (state: RootState) => state.userProfile
  ) as UserProfile;
  const isLoading = userProfile?.status?.message === 'loading';

  const [isEditMode, setIsEditMode] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const {
    mutate: updateUser,
    isPending,
    reset: resetMutation
  } = useUpdateUser();

  const currentApp = findCurrentApp(userProfile?.apps, POOL_NAME);
  const updatedAtFormatted = formatUpdatedAt(currentApp);

  useEffect(() => {
    dispatch(fetchUserProfile({}));
  }, [dispatch]);

  useEffect(() => {
    setBreadcrumbOverride([{ label: 'User Profile', path: '/profile' }]);
    return () => {
      setBreadcrumbOverride(null);
    };
  }, [setBreadcrumbOverride]);

  useEffect(() => {
    if (userProfile?.phone) {
      setPhoneValue(getPhoneDigits(userProfile.phone));
    }
  }, [userProfile?.phone]);

  const handleEditClick = () => {
    setIsEditMode(true);
    setPhoneError('');
  };

  const handleCancelClick = () => {
    setIsEditMode(false);
    setPhoneValue(getPhoneDigits(userProfile?.phone || ''));
    setPhoneError('');
  };

  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, '').substring(0, 10);
    setPhoneValue(digits);
    if (phoneError && validatePhone(digits)) {
      setPhoneError('');
    }
  };

  const handleSaveClick = () => {
    if (!validatePhone(phoneValue)) {
      setPhoneError('Phone number must be 10 digits');
      return;
    }

    const phoneToSave = phoneValue ? `+1${phoneValue}` : '';
    const powerApp = findCurrentApp(userProfile?.apps, POOL_NAME);

    const userData = {
      username: userProfile?.username || '',
      name: userProfile?.name || '',
      email: userProfile?.email || '',
      phone: phoneToSave,
      company: userProfile?.company || '',
      companyId: userProfile?.companyId || '',
      enabled: powerApp?.enabled ?? true,
      groups: userProfile?.groups?.[0] || ''
    };

    updateUser(userData, {
      onSuccess: () => {
        updateModalContent(
          <div className={adminStyles.successModalContainer}>
            <div className={adminStyles.successModalIcon}>
              <SuccessIcon />
            </div>
            <h2 className={adminStyles.successModalTitle}>
              Profile was successfully updated
            </h2>
          </div>,
          {
            modalTitle: '',
            modalButtons: [],
            autoClose: 3000
          }
        );
        setTimeout(() => {
          setIsEditMode(false);
          closeModal();
          dispatch(fetchUserProfile({}));
        }, 2000);
      },
      onError: error => {
        showToast(error?.message || 'An error occurred while saving', {
          type: 'error',
          autoClose: 5000,
          actionText: 'Try again',
          onActionClick: () => {
            resetMutation();
          }
        });
      }
    });
  };

  if (isLoading && !userProfile?.username) {
    return <ContainerLoader />;
  }

  const groupsDisplay = userProfile?.groups?.join(', ') || '';

  return (
    <div className={adminStyles.adminWrapper}>
      <div className={adminStyles.adminContainer}>
        <AdminCardHeader
          left={
            <div className={styles.headerLeft}>
              <UserIcon className={styles.headerIcon} />
              <h2 className={styles.headerTitle}>Personal Details</h2>
              {!isEditMode && (
                <Button
                  onClick={handleEditClick}
                  color="blue"
                  solid={false}
                  label="EDIT"
                  size="small"
                  id="userProfileEditButton"
                />
              )}
            </div>
          }
          right={
            !isEditMode && updatedAtFormatted ? (
              <div className={styles.updatedAtContainer}>
                <span className={styles.updatedAtLabel}>Updated at</span>
                <span className={styles.updatedAtValue}>
                  {updatedAtFormatted}
                </span>
              </div>
            ) : undefined
          }
        />

        <AdminCardBody>
          <div className={styles.detailsGrid}>
            <div className={styles.detailsColumn}>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Name</span>
                <span className={styles.fieldValue}>
                  {userProfile?.name || ''}
                </span>
              </div>

              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Phone Number</span>
                {isEditMode ? (
                  <div className={styles.phoneInputWrapper}>
                    <Input
                      label=""
                      value={formatPhoneNumber(phoneValue)}
                      onChange={handlePhoneChange}
                      withFlag
                      onlyNumbers
                      maxLength={14}
                      error={!!phoneError}
                      customClass={styles.phoneInput}
                      id="userProfilePhoneInput"
                    />
                    {phoneError && (
                      <span className={styles.phoneErrorMessage}>
                        {phoneError}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className={styles.fieldValue}>
                    {formatPhoneDisplay(userProfile?.phone || '')}
                  </span>
                )}
              </div>

              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Email</span>
                <span className={styles.fieldValue}>
                  {userProfile?.email || ''}
                </span>
              </div>
            </div>

            <div className={styles.detailsColumn}>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Company</span>
                <span className={styles.fieldValue}>
                  {userProfile?.company || ''}
                </span>
              </div>

              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Role / Group</span>
                <span className={styles.fieldValue}>{groupsDisplay}</span>
              </div>
            </div>
          </div>
        </AdminCardBody>
      </div>

      {isEditMode && (
        <AdminCardButtons>
          <Button
            onClick={handleCancelClick}
            color="blue"
            solid={false}
            label="CANCEL"
            size="large"
            id="userProfileCancelButton"
          />
          <Button
            onClick={handleSaveClick}
            color="blue"
            label="SAVE"
            size="large"
            loading={isPending}
            id="userProfileSaveButton"
          />
        </AdminCardButtons>
      )}
    </div>
  );
}

export default UserProfileContainer;
